import React, { useState, useRef } from "react";
import {
  Box,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import WavesIcon from "@mui/icons-material/Waves";
import { useTideGraphData } from "../../hooks/useTideGraphData";
import { TideGraphCompact } from "./TideGraphCompact";
import { TideGraphExpanded } from "./TideGraphExpanded";

const TideGraph = ({ station }) => {
  const [expanded, setExpanded] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const lastFormattedDateRef = useRef(null); // Ref for date formatting in XAxis

  const {
    preparedData,
    stationName,
    loading,
    error,
    extremePoints,
    tideStatus,
    currentHeight,
    closestPoint,
    dateRange,
  } = useTideGraphData(station);

  // Toggle expand/collapse with mobile considerations
  const toggleExpanded = () => {
    setExpanded(!expanded);
    // Reset the date formatter ref when toggling to ensure fresh date labels
    lastFormattedDateRef.current = null;
  };

  const chartDimensions = (() => {
    if (expanded) {
      // Expanded mode dimensions
      if (isMobile) {
        return {
          height: 250,
          minWidth: "100%",
          margin: { top: 10, right: 5, left: 5, bottom: 30 },
        };
      } else {
        return {
          height: 300,
          minWidth: "450px",
          margin: { top: 5, right: 20, left: 0, bottom: 20 },
        };
      }
    } else {
      // Compact mode dimensions
      if (isMobile) {
        return {
          height: 120,
          margin: { top: 25, right: 5, left: 5, bottom: 25 },
        };
      } else {
        return {
          height: 140,
          margin: { top: 25, right: 10, left: 10, bottom: 25 },
        };
      }
    }
  })();

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ width: "100%", p: isMobile ? 1 : 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <WavesIcon color="primary" />
          <Typography
            variant={isMobile ? "body2" : "subtitle1"}
            color="primary"
          >
            Tide Graph
          </Typography>
          <Skeleton variant="text" width={100} />
        </Stack>
        <Skeleton
          variant="rectangular"
          height={chartDimensions.height}
          animation="wave"
        />
      </Box>
    );
  }

  // Render error state
  if (error || !preparedData.length) {
    return (
      <Box
        sx={{
          width: "100%",
          p: isMobile ? 1 : 2,
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 1,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <WavesIcon color="disabled" />
          <Typography
            variant={isMobile ? "body2" : "subtitle1"}
            color="textSecondary"
          >
            {error || "No tide data available"}
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Conditionally render compact or expanded view
  return expanded ? (
    <TideGraphExpanded
      stationName={stationName}
      preparedData={preparedData}
      extremePoints={extremePoints}
      tideStatus={tideStatus}
      currentHeight={currentHeight}
      closestPoint={closestPoint}
      dateRange={dateRange}
      toggleExpanded={toggleExpanded}
    />
  ) : (
    <TideGraphCompact
      stationName={stationName}
      preparedData={preparedData}
      extremePoints={extremePoints}
      tideStatus={tideStatus}
      currentHeight={currentHeight}
      closestPoint={closestPoint} // Pass closestPoint to compact for dot logic
      dateRange={dateRange}
      toggleExpanded={toggleExpanded}
    />
  );
};

export default TideGraph;
